import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icoon } from '../ui/Icoon';
import {
  compactChangeNr,
  dagUren,
  eerstMogelijk,
  fmtDag,
  fmtUren,
  normaliseerChangeNr,
  reis,
  reisUren,
  suggesties,
  taakcodes,
  vulSjabloon,
  werkvormVoorCode,
  type TaakType,
  type Werkvorm,
} from '@ep/core';
import { api } from '../lib/api';
import { bestemmingVan, NIVEAUS, useActie, useData, useMag, type TaakRij } from '../lib/data';
import { AdresVeld, type GeoWaarde } from '../ui/AdresVeld';
import { TaakStatusBadge } from '../ui/TakenTabel';
import { getal, Laden, Modal, TIJDEN, Veld, VerwijderKnop } from '../ui/ui';
import { EigenVeldenInvoer } from '../ui/EigenVelden';
import { Gesprek } from '../ui/Gesprek';

const TYPE_LABEL: Record<TaakType, string> = { PROJECT: 'Projecttaak', SERVICE_CALL: 'Service call', OVERLEG: 'Overleg (Teams)', FOCUS: 'Focusblok (sprint)' };

export function TaakScherm() {
  const { id } = useParams();
  const [q] = useSearchParams();
  const { data: d, kal } = useData();
  const mag = useMag();
  const nav = useNavigate();
  const loc = useLocation();
  const kanPlannen = mag('taken.plannen');
  const bestaand = id ? d?.taken.find((t) => t.id === id) : undefined;
  const [f, setF] = useState<FormState | null>(null);

  // formulier vullen zodra de gegevens er zijn
  useEffect(() => {
    if (!d || f) return;
    if (id && !bestaand) return;
    setF(beginwaarden(d, bestaand, q));
  }, [d, id, bestaand, q, f]);

  const plan = useMemo(() => {
    if (!d || !kal || !f) return null;
    const upd = dagUren(f.van, f.tot, d.instellingen);
    const eng = kal.engineer(f.engineerId);
    const dagenLijst = eng && f.startdatum ? kal.planDagen(eng, f.startdatum, Math.max(1, getal(f.dagen) ?? 1)) : [];
    const p = d.projecten.find((x) => x.id === f.projectId);
    const best = f.werkvorm === 'ONSITE' ? (f.geo.adres ? (f.geo.lat != null ? { lat: f.geo.lat, lon: f.geo.lon } : null) : p ? { lat: p.lat, lon: p.lon } : null) : null;
    const r = eng && best ? reis(eng, best, d.instellingen) : null;
    return { upd, dagenLijst, best, reis: r, ru: reisUren(r?.min, d.instellingen) };
  }, [d, kal, f]);

  const sug = useMemo(() => {
    if (!d || !kal || !f || !plan || !kanPlannen) return [];
    const tp = d.takenpakketten.find((x) => x.id === f.takenpakketId);
    return suggesties(
      kal,
      {
        projectId: f.projectId || null,
        expertises: f.expertises.filter((x) => x.naam),
        maxNiveau: tp?.maxNiveau ?? 100,
        start: f.startdatum || d.vandaag,
        dagen: Math.max(1, getal(f.dagen) ?? 1),
        urenPerDag: plan.upd,
        onsite: f.werkvorm === 'ONSITE',
        bestemming: plan.best,
        uitgezonderdTaakId: id ?? null,
      },
      bestemmingVan(d),
    );
  }, [d, kal, f, plan, kanPlannen, id]);

  const opslaan = useActie(
    async () => {
      const b = naarBody(f!);
      return id ? api.put<TaakRij>(`/api/taken/${id}`, b) : api.post<TaakRij>('/api/taken', b);
    },
    {
      succes: (r) => (r.status === 'GEPLAND' ? 'Taak opgeslagen. De Outlook-afspraak volgt binnen een paar minuten.' : 'Taak opgeslagen (nog niet gepland).'),
      opNa: () => terug(),
    },
  );
  const verwijder = useActie(() => api.del(`/api/taken/${id}`), { succes: 'Taak verwijderd', opNa: () => terug() });
  const opnieuw = useActie(() => api.post(`/api/taken/${id}/opnieuw`), { succes: 'Er wordt een nieuwe Outlook-afspraak gemaakt' });

  function terug() {
    // SPEC §4.6: de taak opent als popup boven het scherm waar je vandaan kwam
    const a = (loc.state as { achtergrond?: { pathname: string; search?: string } } | null)?.achtergrond;
    const r = q.get('terug');
    if (a) nav(`${a.pathname}${a.search ?? ''}`, { replace: true });
    else if (r) nav(r, { replace: true });
    else nav('/taken', { replace: true });
  }

  if (!d || !kal || !f || !plan) return <Modal titel="Taak" onSluit={terug}><Laden /></Modal>;
  if (id && !bestaand) return <Modal titel="Taak niet gevonden" onSluit={terug}><p>Deze taak bestaat niet meer. <Link to="/taken">Naar de takenlijst</Link></p></Modal>;
  const zet = <K extends keyof FormState>(k: K, v: FormState[K]) => setF({ ...f, [k]: v });
  const project = d.projecten.find((p) => p.id === f.projectId);
  const wps = d.werkpakketten.filter((w) => w.projectId === f.projectId).sort((a, b) => a.volgorde - b.volgorde);
  const tps = d.takenpakketten.filter((t) => t.projectId === f.projectId && (!f.werkpakketId || t.werkpakketId === f.werkpakketId));
  const codes = taakcodes(d.instellingen);
  const eng = kal.engineer(f.engineerId);
  const td = vulSjabloon(d.instellingen.topdeskSjabloon, { changenr: f.changeNr, changenrCompact: compactChangeNr(f.changeNr) });

  const kiesTakenpakket = (tid: string) => {
    const tp = d.takenpakketten.find((x) => x.id === tid);
    setF({
      ...f,
      takenpakketId: tid,
      werkpakketId: tp?.werkpakketId ?? f.werkpakketId,
      titel: f.titel || tp?.naam || '',
      expertises: tp?.expertise ? [{ naam: tp.expertise, minNiveau: tp.minNiveau }] : f.expertises,
    });
  };
  const kiesCode = (code: string) => {
    const wv = werkvormVoorCode(code);
    setF({ ...f, taakCode: code, werkvorm: wv ?? f.werkvorm });
  };
  const eerste = () => {
    const tp = d.takenpakketten.find((x) => x.id === f.takenpakketId);
    const wp = d.werkpakketten.find((x) => x.id === (tp?.werkpakketId ?? f.werkpakketId));
    zet('startdatum', eerstMogelijk({ vandaag: d.vandaag, takenpakket: tp, werkpakket: wp, taken: d.taken, tps: d.takenpakketten, uitgezonderd: id }));
  };

  return (
    <Modal
      breedte="xl"
      onSluit={terug}
      titel={<>{id ? 'Taak bewerken' : 'Nieuwe taak'} · {TYPE_LABEL[f.taakType]}</>}
      extraKop={
        <>
          {bestaand && <TaakStatusBadge t={bestaand} />}
          {bestaand && (bestaand.outlookMelding ?? '').startsWith('Afspraak verwijderd') && kanPlannen && (
            <button className="knop klein" onClick={() => opnieuw.mutate()}><Icoon naam="refresh-cw" maat={14} /> Nieuwe Outlook-afspraak</button>
          )}
        </>
      }
      voet={
        <>
          {id && kanPlannen && <VerwijderKnop onBevestig={() => verwijder.mutate()} />}
          <div className="groei" />
          <button className="knop" onClick={terug}>Annuleren</button>
          <button className="knop primair" disabled={opslaan.isPending} onClick={() => opslaan.mutate()}><Icoon naam="circle-check" maat={16} /> Opslaan</button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(420px, 680px) 1fr', gap: 16, alignItems: 'start' }}>
        <div className="paneel">
          <div className="grid2">
            <Veld label="Taak *" className="span2">
              <input value={f.titel} onChange={(e) => zet('titel', e.target.value)} placeholder="Wat moet er gebeuren?" />
            </Veld>
            {f.taakType === 'SERVICE_CALL' ? (
              <>
                <Veld label="Klant"><input value={f.klant} onChange={(e) => zet('klant', e.target.value)} /></Veld>
                <Veld label="Change- of incidentnummer" hint="Bijv. SC 2610 03253 of SI 2610 06439 – met of zonder spaties">
                  <div className="rij">
                    <input value={f.changeNr} onChange={(e) => zet('changeNr', e.target.value)} onBlur={() => zet('changeNr', normaliseerChangeNr(f.changeNr))} />
                    {td && f.changeNr && <a className="knop" href={td} target="_blank" rel="noreferrer" title="Openen in TOPdesk"><Icoon naam="link" maat={16} /></a>}
                  </div>
                </Veld>
                <Veld label="SO-nummer / project">
                  <select value={f.projectId} onChange={(e) => setF({ ...f, projectId: e.target.value, klant: f.klant || d.projecten.find((p) => p.id === e.target.value)?.klant || '' })}>
                    <option value="">– geen –</option>
                    {d.projecten.map((p) => <option key={p.id} value={p.id}>{p.nummer} {p.naam}</option>)}
                  </select>
                </Veld>
                <Veld label="SO-nummer (vrij)"><input value={f.soNr} onChange={(e) => zet('soNr', e.target.value)} /></Veld>
              </>
            ) : (
              <>
                <Veld label="Project" className="span2">
                  <select value={f.projectId} onChange={(e) => setF({ ...f, projectId: e.target.value, werkpakketId: '', takenpakketId: '' })}>
                    <option value="">– kies een project –</option>
                    {d.projecten.filter((p) => p.status !== 'AFGEROND' || p.id === f.projectId).map((p) => <option key={p.id} value={p.id}>{p.nummer} {p.naam} – {p.klant}</option>)}
                  </select>
                </Veld>
                {f.taakType === 'PROJECT' && (
                  <>
                    <Veld label="Werkpakket">
                      <select value={f.werkpakketId} onChange={(e) => setF({ ...f, werkpakketId: e.target.value, takenpakketId: '' })} disabled={!f.projectId}>
                        <option value="">– geen / direct onder project –</option>
                        {wps.map((w) => <option key={w.id} value={w.id}>{w.naam}</option>)}
                      </select>
                    </Veld>
                    <Veld label="Takenpakket" hint={(() => {
                      const tp = d.takenpakketten.find((x) => x.id === f.takenpakketId);
                      if (!tp) return undefined;
                      const al = d.taken.filter((t) => t.takenpakketId === tp.id && t.id !== id && t.status === 'GEPLAND').reduce((s, t) => s + (t.urenPerDag ?? 0) * (t.dagen - 1) + (t.urenLaatsteDag ?? 0), 0);
                      return `${fmtUren(al)} van ${fmtUren(tp.uren)} uur al ingepland`;
                    })()}>
                      <select value={f.takenpakketId} onChange={(e) => kiesTakenpakket(e.target.value)} disabled={!f.projectId}>
                        <option value="">– geen (losse taak) –</option>
                        {tps.map((t) => <option key={t.id} value={t.id}>{t.naam} ({t.uren}u)</option>)}
                      </select>
                    </Veld>
                  </>
                )}
              </>
            )}
            <div className="span2 kolom" style={{ gap: 4 }}>
              <span className="klein mute vet">Expertise en minimaal niveau</span>
              {f.expertises.map((x, i) => (
                <div className="rij" key={i}>
                  <select value={x.naam} onChange={(e) => zet('expertises', f.expertises.map((y, j) => (j === i ? { ...y, naam: e.target.value } : y)))}>
                    <option value="">– kies –</option>
                    {d.expertises.map((e) => <option key={e.id} value={e.naam}>{e.naam}</option>)}
                  </select>
                  <select value={x.minNiveau} onChange={(e) => zet('expertises', f.expertises.map((y, j) => (j === i ? { ...y, minNiveau: Number(e.target.value) } : y)))} style={{ maxWidth: 220 }}>
                    <option value={0}>Geen minimum</option>
                    {NIVEAUS.map((n) => <option key={n.p} value={n.p}>min. {n.label}</option>)}
                  </select>
                  <button className="icoon-knop" onClick={() => zet('expertises', f.expertises.filter((_, j) => j !== i))} title="Weghalen"><Icoon naam="trash-2" maat={16} /></button>
                </div>
              ))}
              <div><button className="knop klein" onClick={() => zet('expertises', [...f.expertises, { naam: '', minNiveau: 0 }])}>+ Expertise</button></div>
            </div>
            <Veld label="Engineer" hint={!kanPlannen ? 'Inplannen doet de planner' : !f.engineerId ? 'Leeg laten = nog niet plannen (geen Outlook-afspraak)' : undefined}>
              <select value={f.engineerId} onChange={(e) => zet('engineerId', e.target.value)} disabled={!kanPlannen}>
                <option value="">– nog niet plannen –</option>
                {d.engineers.filter((e) => e.actief !== false || e.id === f.engineerId).map((e) => <option key={e.id} value={e.id}>{e.naam}</option>)}
              </select>
            </Veld>
            <Veld label="Uitvoering (taakcode)">
              <select value={f.taakCode} onChange={(e) => kiesCode(e.target.value)}>
                <option value="">– kies –</option>
                {codes.map((c) => <option key={c.label} value={c.label}>{c.label}</option>)}
              </select>
            </Veld>
            <Veld label="Startdatum">
              <div className="rij">
                <input type="date" value={f.startdatum} onChange={(e) => zet('startdatum', e.target.value)} disabled={!kanPlannen && !!bestaand} />
                {kanPlannen && <button className="knop" title="Eerst mogelijke datum (houdt rekening met afhankelijkheden)" onClick={eerste}><Icoon naam="calendar-days" maat={16} /></button>}
              </div>
            </Veld>
            <Veld label="Aantal werkdagen" hint={plan.dagenLijst.length > 1 ? `t/m ${fmtDag(plan.dagenLijst[plan.dagenLijst.length - 1], 'lang')}` : undefined}>
              <input value={f.dagen} onChange={(e) => zet('dagen', e.target.value)} />
            </Veld>
            <Veld label="Van – tot" hint={`${fmtUren(plan.upd)} uur per dag (na pauze)`}>
              <div className="rij">
                <select value={f.van} onChange={(e) => zet('van', e.target.value)}>{TIJDEN.map((t) => <option key={t}>{t}</option>)}</select>
                <select value={f.tot} onChange={(e) => zet('tot', e.target.value)}>{TIJDEN.map((t) => <option key={t}>{t}</option>)}</select>
              </div>
            </Veld>
            <Veld label="Werkvorm">
              <div className="rij">
                {(['REMOTE', 'ONSITE'] as Werkvorm[]).map((w) => (
                  <button key={w} type="button" className={`knop ${f.werkvorm === w ? 'aan' : ''}`} onClick={() => zet('werkvorm', w)}>
                    {w === 'REMOTE' ? '💻 Remote' : '🚗 Onsite'}
                  </button>
                ))}
              </div>
            </Veld>
            {f.werkvorm === 'ONSITE' && (
              <div className="span2 kolom" style={{ gap: 4 }}>
                <AdresVeld label={`Afwijkend adres (leeg = ${project?.geoNaam ?? project?.locatie ?? 'projectlocatie'})`} waarde={f.geo} onChange={(g) => zet('geo', g)} />
                <span className="klein">
                  {plan.reis ? `🚗 Reistijd ca. ${plan.reis.min} min (${plan.reis.km} km) enkele reis · telt ${fmtUren(plan.ru)} uur per dag mee` : eng ? '⚠ Geen reistijd: adres van engineer of bestemming ontbreekt' : 'Kies een engineer om de reistijd te zien'}
                </span>
              </div>
            )}
            {f.taakType === 'OVERLEG' && (
              <Veld label="Extra deelnemers (e-mailadressen, gescheiden door ;)" className="span2">
                <input value={f.deelnemers} onChange={(e) => zet('deelnemers', e.target.value)} />
              </Veld>
            )}
            <Veld label="Omschrijving / werkzaamheden" className="span2">
              <textarea value={f.omschrijving} onChange={(e) => zet('omschrijving', e.target.value)} />
            </Veld>
          </div>
          {/* SPEC §19 */}
          <EigenVeldenInvoer entiteit="TAAK" waarden={f.velden} onChange={(v) => zet('velden', v)} />
          {bestaand?.terugmelding && (
            <div className="paneel vlak" style={{ marginTop: 12 }}>
              <b>Terugmelding:</b> {bestaand.terugmelding.replace('_', ' ').toLowerCase()}
              {bestaand.tmOpmerking && <div>{bestaand.tmOpmerking}</div>}
              {bestaand.tmRestUren != null && <div>Nog {fmtUren(bestaand.tmRestUren)} uur nodig</div>}
            </div>
          )}
        </div>

        <div className="kolom">
          {kanPlannen && (
            <div className="paneel kolom">
              <div className="rij">
                <h2 className="groei">Slimme suggestie</h2>
                <span className="klein mute" title="Instelbaar bij Beheer › Instellingen">
                  score = {d.instellingen.kennisWeging}% kennis + {100 - d.instellingen.kennisWeging}% beschikbaarheid · +{d.instellingen.bonusProject} zelfde project · +10 in de buurt
                </span>
              </div>
              {sug.map((s) => (
                <div className="suggestie" key={s.engineerId} style={{ borderColor: f.engineerId === s.engineerId ? 'var(--accent)' : undefined }}>
                  <div className="score">{s.score}</div>
                  <div className="groei">
                    <div className="vet">{s.naam}</div>
                    <div className="klein mute">
                      kennis {s.kennis}%{s.mist.length ? ` · mist ${s.mist.join(', ')}` : ''} · past {s.past}/{s.nodig} dagen · {fmtUren(s.vrij)}u vrij
                      {s.reisMin != null && ` · 🚗 ${s.reisMinBest ?? s.reisMin} min`}
                      {s.alOpProject && ' · werkt al aan dit project'}
                      {s.buurt && ` · 📍 die dag al in de buurt (${s.buurtKm} km, ${s.buurtKlant})`}
                    </div>
                    {s.problemen && <div className="klein" style={{ color: 'var(--oranje)' }}>{s.problemen}</div>}
                  </div>
                  <button className="knop" onClick={() => zet('engineerId', s.engineerId)}>Kies</button>
                </div>
              ))}
              {!sug.length && <div className="leeg">Geen passende engineers (controleer expertise, niveau en periode).</div>}
            </div>
          )}
          {eng && plan.dagenLijst.length > 0 && (
            <div className="paneel">
              <h2 style={{ marginBottom: 8 }}>Agenda van {eng.naam}</h2>
              <table className="tabel">
                <thead><tr><th>Dag</th><th className="rechts">Andere taken</th><th className="rechts">Outlook</th><th>Vrij</th></tr></thead>
                <tbody>
                  {plan.dagenLijst.map((dag) => {
                    const cap = kal.capaciteit(eng, dag);
                    const anders = kal.gepland(eng, dag, id);
                    const bz = kal.bezetUren(eng, dag);
                    const vrij = Math.round((cap - anders - bz) * 100) / 100;
                    const nodig = plan.upd + plan.ru;
                    const afw = kal.afwezigOp(eng, dag);
                    return (
                      <tr key={dag}>
                        <td className="vet">{fmtDag(dag)}</td>
                        <td className="rechts">{fmtUren(anders)}u</td>
                        <td className="rechts">{fmtUren(bz)}u</td>
                        <td style={{ color: vrij >= nodig ? 'var(--accent-tekst)' : 'var(--rood)', fontWeight: 600 }}>
                          {afw ? `⚠ afwezig` : vrij >= nodig ? `✓ ${fmtUren(vrij)}u vrij` : `⚠ ${fmtUren(vrij)}u vrij, ${fmtUren(nodig)}u nodig`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      {/* SPEC §18: opmerkingen en bestanden */}
      {id && <div className="paneel" style={{ marginTop: 16 }}><Gesprek entiteit="TAAK" id={id} /></div>}
    </Modal>
  );
}

interface FormState {
  taakType: TaakType;
  titel: string;
  projectId: string;
  werkpakketId: string;
  takenpakketId: string;
  klant: string;
  changeNr: string;
  soNr: string;
  expertises: { naam: string; minNiveau: number }[];
  engineerId: string;
  taakCode: string;
  startdatum: string;
  dagen: string;
  van: string;
  tot: string;
  werkvorm: Werkvorm;
  geo: GeoWaarde;
  deelnemers: string;
  omschrijving: string;
  velden: Record<string, unknown>;
}

function beginwaarden(d: NonNullable<ReturnType<typeof useData>['data']>, t: TaakRij | undefined, q: URLSearchParams): FormState {
  if (t)
    return {
      taakType: t.taakType,
      titel: t.titel,
      projectId: t.projectId ?? '',
      werkpakketId: t.werkpakketId ?? '',
      takenpakketId: t.takenpakketId ?? '',
      klant: t.klant ?? '',
      changeNr: t.changeNr ?? '',
      soNr: t.soNr ?? '',
      expertises: t.expertises ?? [],
      engineerId: t.engineerId ?? '',
      taakCode: t.taakCode ?? '',
      startdatum: t.startdatum ?? '',
      dagen: String(t.dagen ?? 1),
      van: t.van ?? d.instellingen.taakVan,
      tot: t.tot ?? d.instellingen.taakTot,
      werkvorm: t.werkvorm,
      geo: { adres: t.afwAdres ?? '', lat: t.afwLat ?? null, lon: t.afwLon ?? null, naam: t.afwGeo ?? null, gewijzigd: false },
      deelnemers: t.deelnemers ?? '',
      omschrijving: t.omschrijving ?? '',
      velden: t.velden ?? {},
    };
  const type = (q.get('type') as TaakType) || 'PROJECT';
  const tp = d.takenpakketten.find((x) => x.id === q.get('takenpakket'));
  const projectId = q.get('project') ?? tp?.projectId ?? '';
  const codes = taakcodes(d.instellingen);
  const code = type === 'SERVICE_CALL' ? (codes.find((c) => c.code === '8734')?.label ?? '') : (codes[0]?.label ?? '');
  return {
    taakType: type,
    titel: tp?.naam ?? (type === 'OVERLEG' ? 'Overleg' : ''),
    projectId,
    werkpakketId: tp?.werkpakketId ?? q.get('werkpakket') ?? '',
    takenpakketId: tp?.id ?? '',
    klant: d.projecten.find((p) => p.id === projectId)?.klant ?? '',
    changeNr: '',
    soNr: '',
    expertises: tp?.expertise ? [{ naam: tp.expertise, minNiveau: tp.minNiveau }] : [],
    engineerId: '',
    taakCode: code,
    startdatum: q.get('datum') ?? d.vandaag,
    dagen: String(tp?.dagen ?? 1),
    van: type === 'OVERLEG' ? '10:00' : d.instellingen.taakVan,
    tot: type === 'OVERLEG' ? '11:00' : d.instellingen.taakTot,
    werkvorm: werkvormVoorCode(code) ?? 'REMOTE',
    geo: { adres: '', lat: null, lon: null, naam: null, gewijzigd: false },
    deelnemers: '',
    omschrijving: '',
    velden: {},
  };
}

function naarBody(f: FormState) {
  return {
    titel: f.titel,
    taakType: f.taakType,
    projectId: f.projectId || null,
    werkpakketId: f.werkpakketId || null,
    takenpakketId: f.takenpakketId || null,
    klant: f.klant || null,
    changeNr: f.changeNr || null,
    soNr: f.soNr || null,
    expertises: f.expertises.filter((x) => x.naam),
    engineerId: f.engineerId || null,
    taakCode: f.taakCode || null,
    startdatum: f.startdatum || null,
    dagen: Math.max(1, Math.round(getal(f.dagen) ?? 1)),
    van: f.van,
    tot: f.tot,
    werkvorm: f.werkvorm,
    afwAdres: f.werkvorm === 'ONSITE' ? f.geo.adres || null : null,
    ...(f.geo.gewijzigd && f.geo.lat != null ? { afwLat: f.geo.lat, afwLon: f.geo.lon, afwGeo: f.geo.adres } : {}),
    deelnemers: f.deelnemers || null,
    omschrijving: f.omschrijving || null,
    velden: f.velden,
  };
}
